import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    clearConsent,
    ConsentChoice,
    getStoredConsent,
    onConsentChange,
    setConsent,
} from "../analytics/consent";
import { trackConsentDecision } from "../analytics/events";

// Cookie consent banner.
//
// Two things here are compliance requirements rather than design choices, and
// should not be "tidied":
//
//   1. Reject is exactly as prominent as Accept — same size, same weight, same
//      position in the flow. A banner where declining is a greyed-out link is
//      the single most commonly enforced dark pattern in EU/UK cookie rulings.
//   2. There is no dismiss "×". Closing a banner without choosing is not
//      consent, so an X would have to mean reject anyway — which reads as a
//      trick. The visitor answers the question.
//
// Nothing on the site is blocked while the banner is up: analytics simply runs
// cookieless until it is answered. No modal, no overlay, no scroll lock.

export const CookieBanner: React.FC = () => {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        setVisible(getStoredConsent() === null);

        // Reopens the banner when consent is withdrawn from the footer link.
        return onConsentChange((choice) => setVisible(choice === null));
    }, []);

    if (!visible) {
        return null;
    }

    const decide = (choice: ConsentChoice) => {
        // Record the decision first so Consent Mode is updated before the event
        // describing it is sent — an accepted decision should be the first hit
        // that carries a client ID, not the last one that does not.
        setConsent(choice);
        trackConsentDecision(choice);
        setVisible(false);
    };

    const buttonBase: React.CSSProperties = {
        fontFamily: "Medium",
        fontSize: "14px",
        padding: "10px 22px",
        borderRadius: "8px",
        cursor: "pointer",
        border: "1px solid rgba(255, 255, 255, 0.35)",
        whiteSpace: "nowrap",
        flex: "1 1 auto",
        minWidth: "120px",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
    };

    return (
        <div
            // region, not dialog: the banner does not trap focus and does not
            // block the page, and a dialog role that behaves like neither is
            // actively misleading to a screen reader.
            role="region"
            aria-label="Cookie consent"
            style={{
                position: "fixed",
                bottom: 0,
                left: 0,
                right: 0,
                // Above the navbar and every section overlay on the site.
                zIndex: 10000,
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "center",
                gap: "16px",
                padding: "16px 20px",
                boxSizing: "border-box",
                background: "rgba(10, 10, 30, 0.88)",
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
                borderTop: "1px solid rgba(255, 255, 255, 0.18)",
                boxShadow: "0 -4px 16px rgba(0, 0, 0, 0.35)",
                color: "rgba(255, 255, 255, 0.95)",
            }}
        >
            <div
                style={{
                    fontFamily: "Regular",
                    fontSize: "14px",
                    lineHeight: 1.5,
                    // Grows to fill a wide screen but wraps above the buttons on
                    // a phone rather than squeezing them off the edge.
                    flex: "1 1 320px",
                    maxWidth: "720px",
                }}
            >
                We use Google Analytics cookies to understand how people find and use this site.
                They are optional — decline and nothing is stored on your device.{" "}
                <Link
                    to="/privacypolicy"
                    style={{ color: "#FFDD68", textDecoration: "underline" }}
                >
                    Privacy Policy
                </Link>
            </div>

            <div
                style={{
                    display: "flex",
                    gap: "12px",
                    flex: "0 1 auto",
                    flexWrap: "wrap",
                    justifyContent: "center",
                }}
            >
                <button
                    type="button"
                    onClick={() => decide("denied")}
                    style={{
                        ...buttonBase,
                        background: "rgba(255, 255, 255, 0.12)",
                        color: "rgba(255, 255, 255, 0.95)",
                    }}
                >
                    Decline
                </button>
                <button
                    type="button"
                    onClick={() => decide("granted")}
                    style={{
                        ...buttonBase,
                        background: "#FFDD68",
                        color: "#1A2402",
                        border: "1px solid #FFDD68",
                    }}
                >
                    Accept
                </button>
            </div>
        </div>
    );
};

/**
 * Footer link that revokes consent and brings the banner back.
 *
 * GDPR requires withdrawing consent to be as easy as giving it, which in
 * practice means a permanent, visible control — not something buried in the
 * privacy policy. Clearing the stored decision immediately downgrades
 * analytics_storage back to denied; it does not wait for the next page load.
 */
export const CookieSettingsLink: React.FC<{ style?: React.CSSProperties }> = ({ style = {} }) => (
    <span
        role="button"
        tabIndex={0}
        onClick={() => clearConsent()}
        onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
                clearConsent();
            }
        }}
        style={{
            fontFamily: "Regular",
            cursor: "pointer",
            textDecoration: "underline",
            ...style,
        }}
    >
        Cookie Settings
    </span>
);
